import AsyncStorage from '@react-native-async-storage/async-storage';
import type { AuthStackParamList, MainStackParamList } from '../../navigation/types';
import { getScreensForStack } from '../../shared/navigation/screenRegistry';
import type { ScreenRegistryRouteName } from '../../shared/navigation/types';
import {
  AUTH_STACK_INITIAL_ROUTE,
  MAIN_STACK_INITIAL_ROUTE,
} from '../../navigation/rootNavigationAuth';
import { isDevHubClient } from './devHub';

export type DevInitialScreenOption = {
  routeName: ScreenRegistryRouteName;
  name: string;
};

/** Guest / unauthenticated stack — pick one as cold-start entry (dev hub only). */
export const DEV_GUEST_INITIAL_SCREENS: DevInitialScreenOption[] = [
  { routeName: 'Main', name: 'Welcome' },
  { routeName: 'DevTools', name: 'Dev Tools' },
  { routeName: 'Order', name: 'Order' },
  { routeName: 'DailyMenuFlow', name: 'Daily Menu' },
  { routeName: 'Consultation', name: 'Consultation' },
  { routeName: 'Journey', name: 'Journey' },
  { routeName: 'JourneyResult', name: 'Journey Result' },
  { routeName: 'SubscriptionList', name: 'Subscriptions' },
  { routeName: 'AboutUs', name: 'About Us' },
  { routeName: 'HealthInformationSources', name: 'Health Information' },
  { routeName: 'GuestMenuPreview', name: 'Guest Menu Preview' },
  { routeName: 'TermsAndConditions', name: 'Terms and Conditions' },
];

/** Authenticated main stack — pick one as cold-start entry (dev hub only). */
export const DEV_AUTH_INITIAL_SCREENS: DevInitialScreenOption[] = [
  { routeName: 'MainTabs', name: 'Home (tabs)' },
  { routeName: 'Profile', name: 'Profile' },
  { routeName: 'MySubscriptions', name: 'My Subscriptions' },
  { routeName: 'Checkout', name: 'Checkout' },
  { routeName: 'OrderHistory', name: 'Order History' },
  { routeName: 'OrderDetails', name: 'Order Details' },
  { routeName: 'Settings', name: 'Settings' },
  { routeName: 'DevTools', name: 'Dev Tools' },
  { routeName: 'Notifications', name: 'Notifications' },
  { routeName: 'Loyalty', name: 'Loyalty' },
  { routeName: 'AddressLists', name: 'Addresses' },
  { routeName: 'DeliverySchedule', name: 'Delivery Schedule' },
  { routeName: 'Community', name: 'Community' },
  { routeName: 'News', name: 'News' },
  { routeName: 'NewsArticle', name: 'News Article' },
  { routeName: 'AddressForm', name: 'Address Form' },
  { routeName: 'FoodItemDetail', name: 'Food Item Detail' },
  { routeName: 'PaymentWebView', name: 'Payment WebView' },
  { routeName: 'OrderCheckout', name: 'Order Checkout' },
  { routeName: 'UpgradeCheckout', name: 'Upgrade Checkout' },
  { routeName: 'OrderConfirmed', name: 'Order Confirmed' },
  { routeName: 'OrderFailed', name: 'Order Failed' },
  { routeName: 'AllergiesDislikes', name: 'Allergies & Dislikes' },
  { routeName: 'OrderCustomerDetails', name: 'Order Customer Details' },
  { routeName: 'Consultation', name: 'Consultation' },
  { routeName: 'Journey', name: 'Journey' },
  { routeName: 'JourneyResult', name: 'Journey Result' },
  { routeName: 'SubscriptionList', name: 'Subscriptions' },
  { routeName: 'AboutUs', name: 'About Us' },
  { routeName: 'HealthInformationSources', name: 'Health Information' },
  { routeName: 'GuestMenuPreview', name: 'Guest Menu Preview' },
  { routeName: 'TermsAndConditions', name: 'Terms and Conditions' },
];

const DEV_INITIAL_SCREEN_STORAGE_KEY = '@dev_initial_screen_selection';

export type DevInitialScreenConfig = {
  enabled: boolean;
  guestRoute: ScreenRegistryRouteName;
  authRoute: ScreenRegistryRouteName;
};

type DevInitialScreenStorage = {
  enabled?: boolean;
  guestRoute?: string;
  authRoute?: string;
};

const DEFAULT_GUEST_ROUTE = AUTH_STACK_INITIAL_ROUTE;
const DEFAULT_AUTH_ROUTE = MAIN_STACK_INITIAL_ROUTE;

let resolved_config: DevInitialScreenConfig = {
  enabled: false,
  guestRoute: DEFAULT_GUEST_ROUTE,
  authRoute: DEFAULT_AUTH_ROUTE,
};

function guestRouteNames(): Set<string> {
  return new Set(getScreensForStack('auth').map(entry => entry.name));
}

function authRouteNames(): Set<string> {
  return new Set(getScreensForStack('main').map(entry => entry.name));
}

function isKnownGuestRoute(route_name: string | undefined): route_name is ScreenRegistryRouteName {
  return typeof route_name === 'string' && guestRouteNames().has(route_name);
}

function isKnownAuthRoute(route_name: string | undefined): route_name is ScreenRegistryRouteName {
  return typeof route_name === 'string' && authRouteNames().has(route_name);
}

function normalizeStoredConfig(raw: DevInitialScreenStorage | null): DevInitialScreenConfig {
  const guest_route = isKnownGuestRoute(raw?.guestRoute) ? raw.guestRoute : DEFAULT_GUEST_ROUTE;
  const auth_route = isKnownAuthRoute(raw?.authRoute) ? raw.authRoute : DEFAULT_AUTH_ROUTE;
  return {
    enabled: raw?.enabled === true,
    guestRoute: guest_route,
    authRoute: auth_route,
  };
}

export function getDevInitialScreenConfig(): DevInitialScreenConfig {
  return resolved_config;
}

export function getDevGuestInitialScreenOptions(): DevInitialScreenOption[] {
  const allowed = guestRouteNames();
  return DEV_GUEST_INITIAL_SCREENS.filter(option => allowed.has(option.routeName));
}

export function getDevAuthInitialScreenOptions(): DevInitialScreenOption[] {
  const allowed = authRouteNames();
  return DEV_AUTH_INITIAL_SCREENS.filter(option => allowed.has(option.routeName));
}

export function getAuthStackInitialRoute(): keyof AuthStackParamList {
  if (!isDevHubClient() || !resolved_config.enabled) {
    return AUTH_STACK_INITIAL_ROUTE;
  }
  return resolved_config.guestRoute as keyof AuthStackParamList;
}

export function getMainStackInitialRoute(): keyof MainStackParamList {
  if (!isDevHubClient() || !resolved_config.enabled) {
    return MAIN_STACK_INITIAL_ROUTE;
  }
  return resolved_config.authRoute as keyof MainStackParamList;
}

/** Call during app boot before navigators mount (INDEX 0 only). */
export async function resolveDevInitialScreens(): Promise<void> {
  if (!isDevHubClient()) {
    resolved_config = {
      enabled: false,
      guestRoute: DEFAULT_GUEST_ROUTE,
      authRoute: DEFAULT_AUTH_ROUTE,
    };
    return;
  }

  try {
    const stored_raw = await AsyncStorage.getItem(DEV_INITIAL_SCREEN_STORAGE_KEY);
    const stored = stored_raw ? (JSON.parse(stored_raw) as DevInitialScreenStorage) : null;
    resolved_config = normalizeStoredConfig(stored);
  } catch {
    resolved_config = normalizeStoredConfig(null);
  }
}

function configMatches(a: DevInitialScreenConfig, b: DevInitialScreenConfig): boolean {
  return a.enabled === b.enabled && a.guestRoute === b.guestRoute && a.authRoute === b.authRoute;
}

/** Dev hub: persist initial-screen override for the next app cold start / reload. */
export async function setDevInitialScreenConfig(next: DevInitialScreenConfig): Promise<void> {
  if (!isDevHubClient()) {
    return;
  }

  const normalized = normalizeStoredConfig({
    enabled: next.enabled,
    guestRoute: next.guestRoute,
    authRoute: next.authRoute,
  });

  if (configMatches(normalized, resolved_config)) {
    return;
  }

  resolved_config = normalized;

  try {
    await AsyncStorage.setItem(DEV_INITIAL_SCREEN_STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    // best-effort
  }
}
